import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { featureFlags } from '@/featureFlags';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  MarkAllReadResponse,
  Notification,
  NotificationList,
  UnreadCount,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const KEY = {
  unread: ['notifications', 'unread-count'] as const,
  inbox: (unreadOnly: boolean) => ['notifications', 'inbox', unreadOnly] as const,
};

// The bell badge: short poll so a new ORDER_SUBMITTED lights up without refresh
// (the established realtime seam - no SSE/websocket in this repo).
export function useUnreadCount() {
  const authed = useAuthStore((s) => s.isAuthenticated);
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  return useQuery({
    queryKey: KEY.unread,
    queryFn: async () =>
      unwrapResult<UnreadCount>(await openapiClient.GET('/api/notifications/unread-count')),
    // Inbox is feature-flagged off on deployed builds - skip the poll entirely so
    // every badge that reads this count stays empty without per-site gating.
    enabled: authed && !!restaurantId && featureFlags.inbox,
    refetchInterval: 30_000,
  });
}

export function useInbox(unreadOnly: boolean) {
  const authed = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: KEY.inbox(unreadOnly),
    queryFn: async () =>
      unwrapResult<NotificationList>(
        await openapiClient.GET('/api/notifications', {
          params: { query: { unreadOnly, take: 30 } },
        }),
      ),
    enabled: authed,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapResult<Notification>(
        await openapiClient.PATCH('/api/notifications/{id}/read', { params: { path: { id } } }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrapResult<MarkAllReadResponse>(await openapiClient.PATCH('/api/notifications/read-all')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
